import React from 'react';
import { Card, Col, Row, Spin, Statistic, Typography } from 'antd';
import PropTypes from 'prop-types';

import { ArrowDownOutlined, ArrowUpOutlined, TrophyOutlined } from '@ant-design/icons';

import EquityCurveChart from './EquityCurveChart';

import './styles.scss';

const { Title, Text } = Typography;

export default function StrategyStatistics({ stats, isLoading, selectedPlan }) {
    if (isLoading || !stats) {
        return (
            <div className="strategy-statistics loading">
                <Spin size="large" />
            </div>
        );
    }

    const formatCurrency = (value) =>
        new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2
        }).format(value);

    return (
        <div className="strategy-statistics">
            <div className="stats-header">
                <Title level={4}>
                    {selectedPlan ? 'Strategy Statistics' : 'All Trades Statistics'}
                </Title>
            </div>

            {/* Key Metrics */}
            <Row gutter={[16, 16]}>
                <Col span={12}>
                    <Card>
                        <Statistic
                            title="Total Trades"
                            value={stats.totalTrades}
                            valueStyle={{ color: '#3f8600' }}
                        />
                    </Card>
                </Col>
                <Col span={12}>
                    <Card>
                        <Statistic
                            title="Win Rate"
                            value={stats.winRate}
                            precision={2}
                            suffix="%"
                            valueStyle={{ color: stats.winRate >= 50 ? '#3f8600' : '#cf1322' }}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col span={12}>
                    <Card>
                        <Statistic
                            title="Avg Win"
                            value={stats.averageWin}
                            prefix={<ArrowUpOutlined />}
                            precision={2}
                            valueStyle={{ color: '#3f8600' }}
                            formatter={(value) => formatCurrency(value)}
                        />
                    </Card>
                </Col>
                <Col span={12}>
                    <Card>
                        <Statistic
                            title="Avg Loss"
                            value={Math.abs(stats.averageLoss)}
                            prefix={<ArrowDownOutlined />}
                            precision={2}
                            valueStyle={{ color: '#cf1322' }}
                            formatter={(value) => formatCurrency(value)}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col span={12}>
                    <Card>
                        <Statistic
                            title="Win:Loss Ratio"
                            value={stats.winLossRatio}
                            precision={2}
                            suffix=":1"
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col span={12}>
                    <Card>
                        <Statistic
                            title="Avg Trades/Day"
                            value={stats.averageTradesPerDay}
                            precision={1}
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Card>
                        <Statistic
                            title="Total P&L"
                            value={stats.totalPnL}
                            precision={2}
                            prefix={
                                stats.totalPnL >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />
                            }
                            valueStyle={{ color: stats.totalPnL >= 0 ? '#3f8600' : '#cf1322' }}
                            formatter={(value) => formatCurrency(value)}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Card>
                        <Statistic
                            title="Discipline %"
                            value={stats.disciplinePercentage}
                            precision={2}
                            suffix="%"
                            prefix={<TrophyOutlined />}
                            valueStyle={{ color: '#faad14' }}
                        />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Percentage of disciplined trades (Cat 1 + Cat 2)
                        </Text>
                    </Card>
                </Col>
            </Row>

            {/* Category Breakdown */}
            {stats.categoryBreakdown && (
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Card title="Trade Category Breakdown">
                            <Row gutter={[16, 8]}>
                                <Col span={6}>
                                    <Statistic
                                        title="Cat 1"
                                        value={stats.categoryBreakdown.cat1}
                                        valueStyle={{ color: '#52c41a' }}
                                    />
                                </Col>
                                <Col span={6}>
                                    <Statistic
                                        title="Cat 2"
                                        value={stats.categoryBreakdown.cat2}
                                        valueStyle={{ color: '#73d13d' }}
                                    />
                                </Col>
                                <Col span={6}>
                                    <Statistic
                                        title="Cat 3"
                                        value={stats.categoryBreakdown.cat3}
                                        valueStyle={{ color: '#faad14' }}
                                    />
                                </Col>
                                <Col span={6}>
                                    <Statistic
                                        title="Cat 4"
                                        value={stats.categoryBreakdown.cat4}
                                        valueStyle={{ color: '#ff4d4f' }}
                                    />
                                </Col>
                            </Row>
                            {stats.categoryBreakdown.untagged > 0 && (
                                <Text type="secondary" style={{ marginTop: 8, display: 'block' }}>
                                    {stats.categoryBreakdown.untagged} trades not yet categorized
                                </Text>
                            )}
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Equity Curve */}
            {stats.equityCurve && stats.equityCurve.length > 0 && (
                <Card title="Equity Curve" style={{ marginTop: 16 }}>
                    <EquityCurveChart data={stats.equityCurve} />
                </Card>
            )}
        </div>
    );
}

StrategyStatistics.propTypes = {
    stats: PropTypes.object,
    isLoading: PropTypes.bool,
    selectedPlan: PropTypes.string
};
